import React, { memo } from "react";
import { Modal as AntModal } from "antd";
import type { ModalProps as AntModalProps } from "antd";
import { CustomButton } from "src/components/ui/Button";
import { useTranslation } from "react-i18next";

export interface CustomModalProps extends Omit<AntModalProps, "footer"> {
  okText?: string;
  cancelText?: string;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void;
  hideOkButton?: boolean;
  hideCancelButton?: boolean;
  showFooter?: boolean;
  footer?: React.ReactNode;
  okBtnLoading?: boolean;
}

const CustomModal: React.FC<CustomModalProps> = ({
  okText,
  cancelText,
  onOk,
  onCancel: onCancelProp,
  hideOkButton = false,
  hideCancelButton = false,
  showFooter = true,
  footer: customFooter,
  okBtnLoading = false,
  ...rest
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const onCancelHandler = onCancelProp ?? (rest as AntModalProps).onCancel;

  const handleOk = async () => {
    if (!onOk) return;
    setLoading(true);
    try {
      await onOk();
      onCancelHandler?.({} as React.MouseEvent<HTMLButtonElement>);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    onCancelHandler?.({} as React.MouseEvent<HTMLButtonElement>);
  };

  const footer =
    customFooter !== undefined
      ? customFooter
      : showFooter
        ? [
            !hideCancelButton && (
              <CustomButton
                key="cancel"
                type="secondary"
                onClick={handleCancelClick}
              >
                {cancelText ?? t("common.cancel")}
              </CustomButton>
            ),
            !hideOkButton && (
              <CustomButton key="ok" type="primary" onClick={handleOk} loading={okBtnLoading || loading}>
                {okText ?? t("common.save")}
              </CustomButton>
            ),
          ].filter(Boolean)
        : null;

  return <AntModal footer={footer} onCancel={onCancelHandler} {...rest} />;
};

export default memo(CustomModal);
